import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../../axiosInstance";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import LoadingTable from "../../components/LoadingTable";

const reportConfig = {
  passbook: {
    title: "Passbook",
    endpoint: "/admin/reports/passbook",
    columns: [
      { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) },
      { key: "t_id", label: "Transaction No" },
      { key: "driver", label: "Driver", render: (row) => row.user?.username || "-" },
      { key: "phone", label: "Phone", render: (row) => row.user?.phone || "-", exportRender: (row) => formatExcelText(row.user?.phone) },
      { key: "note", label: "Note" },
      { key: "amount", label: "Amount", render: (row) => formatAmount(row.amount) },
      { key: "type", label: "Type", render: (row) => (Number(row.type) === 1 ? "Credit" : "Debit") },
    ],
  },
  drivers: {
    title: "Drivers Reports",
    endpoint: "/admin/reports/drivers",
    columns: [
      { key: "driverId", label: "Driver Id" },
      { key: "driverName", label: "Driver Name" },
      { key: "noOfDuty", label: "No of Duty" },
      { key: "totalCommission", label: "Total Comission", render: (row) => formatAmount(row.totalCommission) },
      { key: "totalRevenue", label: "Total Revenue", render: (row) => formatAmount(row.totalRevenue) },
      { key: "totalGst", label: "Total GST", render: (row) => formatAmount(row.totalGst) },
    ],
  },
  invoices: {
    title: "Invoice Reports",
    endpoint: "/admin/reports/invoices",
    columns: [
      { key: "date", label: "DATE", render: (row) => formatDate(row.date) },
      { key: "invoiceNo", label: "INVOICE NO" },
      { key: "driverName", label: "DRIVER NAME" },
      { key: "driverState", label: "DRIVER STATE" },
      { key: "convenienceFees", label: "CONVINENCE FEES", render: (row) => formatAmount(row.convenienceFees) },
      { key: "igst", label: "IGST", render: (row) => formatAmount(row.igst) },
      { key: "cgst", label: "CGST", render: (row) => formatAmount(row.cgst) },
      { key: "sgst", label: "SGST", render: (row) => formatAmount(row.sgst) },
    ],
  },
  ratings: {
    title: "Rating Reports",
    endpoint: "/admin/reports/ratings",
    columns: [
      { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) },
      { key: "orderId", label: "Order Id", render: (row) => row.order?.orderId || "-" },
      { key: "reviewFor", label: "Review For", render: (row) => formatTitle(row.reviewFor) },
      { key: "reviewer", label: "User", render: (row) => formatPerson(row.reviewer), exportRender: (row) => formatPerson(row.reviewer, " | ") },
      { key: "reviewee", label: "Driver / Staff", render: (row) => formatPerson(row.reviewee), exportRender: (row) => formatPerson(row.reviewee, " | ") },
      { key: "rating", label: "Rating", render: (row) => `${row.rating || 0}/5` },
      { key: "comment", label: "Comment" },
      { key: "status", label: "Status", render: (row) => (String(row.status) === "0" ? "Hide" : "Show") },
    ],
  },
};

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escapeCsvValue(value) {
  const text = value === undefined || value === null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function formatExcelText(value) {
  if (value === undefined || value === null || value === "") return "";
  return `="${String(value).replace(/"/g, '""')}"`;
}

function formatUserStatus(status) {
  return String(status) === "0" ? "Inactive" : "Active";
}

function formatTitle(value) {
  if (!value) return "-";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

function formatPerson(person, separator = "\n") {
  if (!person) return "-";
  const text = [person.username || person.userId || "-", person.phone || ""].filter(Boolean).join(separator);
  if (separator !== "\n") return text;
  return <div style={{ whiteSpace: "pre-line" }}>{text}</div>;
}

function AdminReport({ type }) {
  const config = reportConfig[type] || reportConfig.passbook;
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  const driverOptions = useMemo(
    () =>
      drivers
        .map((driver) => ({
          value: driver._id,
          label: `${driver.username || "Driver"}${driver.phone ? ` (${driver.phone})` : ""} - ${formatUserStatus(driver.status)}`,
        })),
    [drivers]
  );

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user._id,
        label: `${user.username || "User"}${user.phone ? ` (${user.phone})` : ""} - ${formatUserStatus(user.status)}`,
      })),
    [users]
  );

  const fetchDrivers = async () => {
    try {
      const response = await axiosInstance.get("/admin/all-user?page=1&limit=1000&type=1&empType=2");
      setDrivers(response.data.users || []);
    } catch (error) {
      setDrivers([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/admin/all-user?page=1&limit=1000&type=2");
      setUsers(response.data.users || []);
    } catch (error) {
      setUsers([]);
    }
  };

  const buildParams = (page = currentPage, pageLimit = limit) =>
    new URLSearchParams({
      page,
      limit: pageLimit,
      search: searchTerm,
      fromDate,
      toDate,
      driverId: selectedDriver?.value || "",
      userId: type === "ratings" ? selectedUser?.value || "" : "",
    });

  const fetchData = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = buildParams(page, limit);
      const response = await axiosInstance.get(`${config.endpoint}?${params.toString()}`);
      setRows(response.data.data || []);
      setCurrentPage(response.data.currentPage || 1);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchData();
  }, [type, currentPage, limit, selectedDriver, selectedUser, fromDate, toDate]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData(1);
  };

  const clearFilters = () => {
    setSelectedDriver(null);
    setSelectedUser(null);
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = buildParams(1, 100000);
      const response = await axiosInstance.get(`${config.endpoint}?${params.toString()}`);
      const exportRows = response.data.data || [];
      const header = config.columns.map((column) => escapeCsvValue(column.label)).join(",");
      const body = exportRows.map((row) =>
        config.columns
          .map((column) => {
            const value = column.exportRender
              ? column.exportRender(row)
              : column.render
                ? column.render(row)
                : row[column.key] || "";

            return escapeCsvValue(value);
          })
          .join(",")
      );
      const csv = ["\ufeff" + header, ...body].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const dateText = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute("download", `${type}-report-${dateText}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting report:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap">
            <div className="mb-3 mb-lg-0">
              <h1 className="h4">{config.title}</h1>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label">From Date</label>
                <input className="form-control" type="date" value={fromDate} onChange={(event) => {
                  setCurrentPage(1);
                  setFromDate(event.target.value);
                }} />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label">To Date</label>
                <input className="form-control" type="date" value={toDate} onChange={(event) => {
                  setCurrentPage(1);
                  setToDate(event.target.value);
                }} />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label">Select Driver</label>
                <Select
                  isClearable
                  options={driverOptions}
                  value={selectedDriver}
                  onChange={(option) => {
                    setCurrentPage(1);
                    setSelectedDriver(option);
                  }}
                  placeholder="Select Driver"
                />
              </div>
              {type === "ratings" && (
                <div className="col-12 col-md-3">
                  <label className="form-label">Select User</label>
                  <Select
                    isClearable
                    options={userOptions}
                    value={selectedUser}
                    onChange={(option) => {
                      setCurrentPage(1);
                      setSelectedUser(option);
                    }}
                    placeholder="Select User"
                  />
                </div>
              )}
              <div className="col-12 col-md-3">
                <label className="form-label">Search</label>
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSearch();
                    }}
                  />
                  <button className="btn btn-primary" type="button" onClick={handleSearch}>Search</button>
                </div>
              </div>
              <div className="col-12">
                <button className="btn btn-outline-secondary" type="button" onClick={clearFilters}>Clear Filters</button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="table-responsive py-4">
            {loading ? (
              <LoadingTable />
            ) : (
              <>
                <div className="datatable-top">
                  <div className="datatable-dropdown">
                    <label>
                      <select
                        className="datatable-selector"
                        value={limit}
                        onChange={(event) => {
                          setLimit(parseInt(event.target.value, 10));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={25}>25</option>
                      </select>{" "}
                      entries per page
                    </label>
                  </div>
                  <div className="datatable-search">
                    <button className="btn btn-success" type="button" onClick={handleExport} disabled={exporting || loading}>
                      {exporting ? "Exporting..." : "Export Excel"}
                    </button>
                  </div>
                </div>
                <table className="table table-flush">
                  <thead>
                    <tr>
                      {config.columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={config.columns.length} className="text-center">No report data found</td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr key={row._id || row.invoiceNo || row.driverMongoId || index}>
                          {config.columns.map((column) => (
                            <td key={column.key}>{column.render ? column.render(row) : row[column.key] || "-"}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className="datatable-bottom">
                  <div className="datatable-info" />
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className={`datatable-pagination-list-item ${currentPage === 1 ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={loading || currentPage === 1}>
                          &lsaquo;
                        </button>
                      </li>
                      <li>
                        <div className="datatable-info py-1 px-3">
                          <strong>Showing Page {currentPage} of {totalPages || 1}</strong>
                        </div>
                      </li>
                      <li className={`datatable-pagination-list-item ${currentPage === totalPages ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={loading || currentPage === totalPages || totalPages === 0}>
                          &rsaquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

export default AdminReport;
